import { useMemo, useState, useCallback } from 'react'
import dayjs from 'dayjs'
import { formatPercent } from '@/lib/format-utils'
import { useRoasStore } from '@/stores/roas'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { LineChartIcon, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { ChartCodeViewer } from '@/components/zthd/chart-code-viewer'
import RetChart from './retention-chart'
import {
  IndexColumn,
  InstallDateColumn,
  InstallsColumn,
  ActiveInstallsColumn,
} from './roas-table-columns'
import {
  ROASDailyDataTable,
  baseSortedData,
  TargetDays,
} from './base-virtualized-table'
import { exportToCsv } from '@/lib/utils'
import { shortCampaignLabel } from '../utils'

const DayNHeader = ({
  dayIndex,
  average,
  handlerDayNClick,
  sorts,
  handleSort,
}) => {
  const sortCode = `ret_day${dayIndex}`
  const currentSort = sorts?.find((s) => s.code === sortCode)
  const isAsc = currentSort?.order === 'asc'
  const isDesc = currentSort?.order === 'desc'

  const handleSortClick = (e) => {
    e.stopPropagation()
    let newOrder
    if (!currentSort) {
      newOrder = 'desc'
    } else if (isDesc) {
      newOrder = 'asc'
    } else {
      newOrder = null
    }

    if (newOrder === null) {
      const newSorts = sorts.filter((s) => {
        if (s.code === sortCode) return false
        if (s.code.startsWith('ret_day')) return false
        return true
      })
      if (newSorts.length === 0) {
        handleSort([{ code: 'install_date', order: 'desc' }])
      } else {
        handleSort(newSorts)
      }
    } else {
      const nonDayNSorts = sorts.filter((s) => !s.code.startsWith('ret_day'))
      handleSort([{ code: sortCode, order: newOrder }, ...nonDayNSorts])
    }
  }

  return (
    <div className="flex flex-col items-end">
      <div className="flex items-center text-base text-black gap-1">
        <button
          onClick={handleSortClick}
          className="flex items-center justify-center p-0.5 hover:bg-gray-100 rounded transition-colors"
          title={isAsc ? '升序' : isDesc ? '降序' : '默认'}
        >
          {isAsc && <ArrowUp className="w-4 h-4" />}
          {isDesc && <ArrowDown className="w-4 h-4" />}
          {!isAsc && !isDesc && (
            <ArrowUpDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
        <div
          className="flex items-center gap-1 cursor-pointer"
          onClick={() => handlerDayNClick(dayIndex)}
        >
          <span>D{dayIndex}</span>
          <LineChartIcon className="w-4 h-4" />
        </div>
      </div>
      {average && (
        <span className="text-right text-xs text-gray-500">{average}</span>
      )}
    </div>
  )
}

const DayRetCell = ({ dayIndex, row }) => {
  const { retDisplayMode, userSourceType, denominatorSource } = useRoasStore()
  const showPercentage = retDisplayMode === 'percentage'
  const showActive = userSourceType === 'active'
  const isFirebase = denominatorSource === 'Firebase'

  const installDays = row.installDays || {}
  const activeDays = row.activeDays || {}
  const installs = isFirebase ? row.activeInstalls || 0 : row.installs

  let value = showActive
    ? activeDays[`day${dayIndex}`]
    : installDays[`day${dayIndex}`] || 0

  // Convert to number to remove leading zeros
  value = Number(value)

  // Check if data exists, not just if value is 0
  const hasData = showActive
    ? activeDays[`day${dayIndex}`] !== undefined
    : installDays[`day${dayIndex}`] !== undefined

  if (!hasData || isNaN(value)) {
    return <div className={`text-right text-base text-black`}></div>
  }

  if (!showPercentage) {
    return <div className={`text-right text-base text-black`}>{value}</div>
  }

  return (
    <div className={`text-right text-base text-black`}>
      {formatPercent(value / installs)}
    </div>
  )
}

const RetTable = ({
  roas,
  compareRoas,
  isTotal = false,
  simplifiedMode,
  summaryMode,
}) => {
  const [sorts, setSorts] = useState([{ code: 'install_date', order: 'desc' }])
  const [isChartModalOpen, setIsChartModalOpen] = useState(false)
  const [chartDayIndex, setChartDayIndex] = useState(null)
  const {
    weekFilter,
    retDisplayMode,
    userSourceType,
    denominatorSource,
    dayOrder,
    setDayOrder,
  } = useRoasStore()

  const handleSort = useCallback(
    (newSorts) => {
      setSorts(newSorts)
      const installDateSort = newSorts.find((s) => s.code === 'install_date')
      if (installDateSort) {
        setDayOrder(installDateSort.order === 'desc' ? 'asc' : 'desc')
      }
    },
    [setDayOrder],
  )

  const handlerDayNClick = useCallback((dayIndex) => {
    setIsChartModalOpen(true)
    setChartDayIndex(dayIndex)
  }, [])

  const getDayNumericValue = useCallback(
    (row, dayIndex) => {
      const showPercentage = retDisplayMode === 'percentage'
      const showActive = userSourceType === 'active'
      const isFirebase = denominatorSource === 'Firebase'

      const installDays = row.installDays || {}
      const activeDays = row.activeDays || {}
      const installs = isFirebase ? row.activeInstalls || 0 : row.installs

      let value = showActive
        ? activeDays[`day${dayIndex}`]
        : installDays[`day${dayIndex}`] || 0

      const hasData = showActive
        ? activeDays[`day${dayIndex}`] !== undefined
        : installDays[`day${dayIndex}`] !== undefined

      if (!hasData) return null

      value = Number(value)
      if (isNaN(value)) return null

      if (showPercentage && installs) {
        return value / installs
      }
      return value
    },
    [retDisplayMode, userSourceType, denominatorSource],
  )

  const sortedData = useMemo(() => {
    const applySort = (data) => {
      const hasDayNSort = sorts.some((s) => s.code.startsWith('ret_day'))

      if (!hasDayNSort) {
        return baseSortedData(data, weekFilter, sorts)
      }

      return [...data].sort((a, b) => {
        for (const sortItem of sorts) {
          const { code, order } = sortItem

          if (code.startsWith('ret_day')) {
            const dayIndex = parseInt(code.replace('ret_day', ''), 10)
            const aValue = getDayNumericValue(a, dayIndex)
            const bValue = getDayNumericValue(b, dayIndex)

            if (aValue === bValue) continue

            // Put nulls at the end
            if (aValue === null) return 1
            if (bValue === null) return -1

            if (order === 'asc') {
              return aValue > bValue ? 1 : -1
            } else {
              return aValue < bValue ? 1 : -1
            }
          }

          // Fallback to basic sorting for other columns
          let aValue = a[code]
          let bValue = b[code]
          if (code === 'install_date') {
            aValue = String(aValue).replace(/-/g, '')
            bValue = String(bValue).replace(/-/g, '')
          }
          if (aValue === bValue) continue
          if (order === 'asc') {
            return aValue > bValue ? 1 : -1
          } else {
            return aValue < bValue ? 1 : -1
          }
        }
        return 0
      })
    }

    const mainSorted = applySort(roas.dailyData)

    if (
      compareRoas &&
      (Array.isArray(compareRoas)
        ? compareRoas.length > 0
        : compareRoas.dailyData)
    ) {
      const compareCampaigns = Array.isArray(compareRoas)
        ? compareRoas
        : [compareRoas]
      const compareDataMaps = compareCampaigns.map((c) => {
        if (c.isDateCompare) return null
        const map = new Map()
        c?.dailyData?.forEach((row) => {
          map.set(row.install_date, row)
        })
        return map
      })
      const sortedCompareByIndex = compareCampaigns.map((c) =>
        c.isDateCompare && c.dailyData?.length ? applySort(c.dailyData) : null,
      )

      const combined = []
      mainSorted.forEach((row, mainIndex) => {
        combined.push({
          ...row,
          type: 'main',
          rowId: `${row.install_date}_main`,
        })
        compareCampaigns.forEach((c, idx) => {
          const compareRow = c.isDateCompare
            ? (sortedCompareByIndex[idx]?.[mainIndex] ?? null)
            : (compareDataMaps[idx]?.get(row.install_date) ?? null)
          if (compareRow) {
            combined.push({
              ...compareRow,
              type: 'compare',
              rowId: c.isDateCompare
                ? `idx${mainIndex}_compare_${idx}`
                : `${row.install_date}_compare_${idx}`,
              campaignName: c.campaignName || 'Compare',
            })
            if (!c.isDateCompare && compareDataMaps[idx]) {
              compareDataMaps[idx].delete(row.install_date)
            }
          }
        })
      })

      compareDataMaps.forEach((map, idx) => {
        if (!map) return
        const leftovers = Array.from(map.values()).sort((a, b) =>
          b.install_date.localeCompare(a.install_date),
        )
        leftovers.forEach((row) => {
          const existingDateIndex = combined.findLastIndex(
            (r) => r.install_date === row.install_date,
          )
          if (existingDateIndex !== -1) {
            combined.splice(existingDateIndex + 1, 0, {
              ...row,
              type: 'compare',
              rowId: `${row.install_date}_compare_${idx}`,
              campaignName: compareCampaigns[idx].campaignName || 'Compare',
            })
          } else {
            combined.push({
              ...row,
              type: 'compare',
              rowId: `${row.install_date}_compare_${idx}`,
              campaignName: compareCampaigns[idx].campaignName || 'Compare',
            })
          }
        })
      })

      return combined
    }

    return mainSorted.map((row) => ({
      ...row,
      type: 'main',
      rowId: `${row.install_date}_main`,
    }))
  }, [roas.dailyData, compareRoas, weekFilter, sorts, getDayNumericValue])

  const retentionDays = useMemo(() => {
    let indices = []
    const useSimplified = simplifiedMode ?? isTotal
    if (useSimplified) {
      indices = TargetDays
    } else {
      const maxDay = roas?.dailyData?.length ?? 7
      indices = Array.from({ length: maxDay }, (_, i) => i)
    }
    return dayOrder === 'desc' ? [...indices].reverse() : indices
  }, [isTotal, simplifiedMode, roas?.dailyData?.length, dayOrder])

  const columnAverages = useMemo(() => {
    const averages = {}
    const showPercentage = retDisplayMode === 'percentage'
    const showActive = userSourceType === 'active'
    const isFirebase = denominatorSource === 'Firebase'

    // Sort by date descending to get recent data
    const recentRows = [...sortedData].sort((a, b) =>
      (b.install_date || '').localeCompare(a.install_date || ''),
    )

    retentionDays.forEach((dayIndex) => {
      let totalValue = 0
      let totalInstalls = 0
      let count = 0

      for (const row of recentRows) {
        if (count >= 30) break

        const installDays = row.installDays || {}
        const activeDays = row.activeDays || {}
        const installs = isFirebase
          ? row.activeInstalls || 0
          : row.installs || 0

        const hasData = showActive
          ? activeDays[`day${dayIndex}`] !== undefined
          : installDays[`day${dayIndex}`] !== undefined

        if (!hasData) continue

        const val = showActive
          ? Number(activeDays[`day${dayIndex}`]) || 0
          : Number(installDays[`day${dayIndex}`]) || 0

        totalValue += val
        totalInstalls += installs
        count++
      }

      if (showPercentage) {
        averages[dayIndex] = totalInstalls
          ? formatPercent(totalValue / totalInstalls)
          : '-'
      } else {
        averages[dayIndex] = count ? (totalValue / count).toFixed(1) : '-'
      }
    })
    return averages
  }, [
    sortedData,
    retentionDays,
    retDisplayMode,
    userSourceType,
    denominatorSource,
  ])

  const handleExportCsv = useCallback(() => {
    if (!sortedData || sortedData.length === 0) {
      return
    }

    const filename = `roas_ret_${dayjs().format('YYYYMMDDHHmmss')}.csv`
    const exportRows = sortedData.map((row) => {
      const exportRow = {}
      const isFirebase = denominatorSource === 'Firebase'

      exportRow['Install Date'] = row.install_date
        ? Number(row.install_date.replaceAll('-', ''))
        : ''

      const installsValue = isFirebase
        ? row.activeInstalls || 0
        : row.installs || 0

      exportRow['Installs'] = row.installs
      exportRow['Active Installs'] = row.activeInstalls

      retentionDays.forEach((dayIndex) => {
        const installDays = row.installDays || {}
        const activeDays = row.activeDays || {}
        const baseValue =
          userSourceType === 'active'
            ? activeDays[`day${dayIndex}`]
            : installDays[`day${dayIndex}`]

        if (baseValue === undefined || baseValue === null || baseValue === 0) {
          exportRow[`Day ${dayIndex}`] = ''
          return
        }

        if (retDisplayMode === 'percentage') {
          if (!installsValue) {
            exportRow[`Day ${dayIndex}`] = ''
            return
          }
          exportRow[`Day ${dayIndex}`] = Number(
            (baseValue / installsValue).toFixed(4),
          )
          return
        }

        exportRow[`Day ${dayIndex}`] = baseValue
      })

      return exportRow
    })

    exportToCsv(exportRows, filename)
  }, [
    sortedData,
    retentionDays,
    retDisplayMode,
    userSourceType,
    denominatorSource,
  ])

  const columns = useMemo(() => {
    const maxDay = roas?.dailyData?.length ?? 7
    const dayCount = Math.max(maxDay, 7)

    // Helper for compare row styling
    const renderCell = (content, row) => {
      if (row.type === 'compare') {
        return <div className="text-muted-foreground italic">{content}</div>
      }
      return content
    }

    const baseColumns = [
      {
        ...IndexColumn,
        render: (value, row, rowIndex) => {
          if (row.type === 'compare') return null
          return (
            <div className="text-right text-base text-black">
              {row.index || rowIndex + 1}
            </div>
          )
        },
      },
      {
        ...InstallDateColumn(
          roas.campaignId,
          handleSort,
          sorts,
          null,
          compareRoas,
          summaryMode,
          roas.dailyData,
        ),
        render: (value, row) => {
          if (row.type === 'compare') {
            const dateLabel = row.displayDate || row.install_date || ''
            const campaignShort = row.campaignName ? shortCampaignLabel(row.campaignName) : ''
            const label = campaignShort ? `${dateLabel} · ${campaignShort}` : dateLabel
            return (
              <div className="pl-5 text-muted-foreground text-sm flex items-center h-full italic" title={row.campaignName || dateLabel}>
                {label}
              </div>
            )
          }
          return (
            <div className="relative">
              {InstallDateColumn(
                roas.campaignId,
                handleSort,
                sorts,
                null,
                compareRoas,
                summaryMode,
                roas.dailyData,
              ).render(value, row)}
            </div>
          )
        },
      },
      {
        ...InstallsColumn(isTotal),
        lock: true,
        render: (value, row) =>
          renderCell(InstallsColumn(isTotal).render(value, row), row),
      },
      {
        ...ActiveInstallsColumn(isTotal),
        lock: true,
        render: (value, row) =>
          renderCell(ActiveInstallsColumn(isTotal).render(value, row), row),
      },
    ]

    // Add retention day columns (Day 0 to Day 6, then Day 7, 14, 30, etc.)
    const dayColumns = retentionDays
      .filter((dayIndex) => dayIndex <= dayCount)
      .map((dayIndex) => ({
        code: `ret_day${dayIndex}`,
        width: 100,
        align: 'right',
        title: (
          <DayNHeader
            dayIndex={dayIndex}
            average={columnAverages[dayIndex]}
            handlerDayNClick={handlerDayNClick}
            sorts={sorts}
            handleSort={handleSort}
          />
        ),
        render: (value, row) =>
          renderCell(<DayRetCell dayIndex={dayIndex} row={row} />, row),
      }))

    return [...baseColumns, ...dayColumns]
  }, [
    roas?.campaignId,
    roas?.dailyData?.length,
    isTotal,
    sorts,
    retentionDays,
    sortedData.length,
    columnAverages,
    denominatorSource, // Kept to force re-render when denominator source changes, though columns structure is static now
    handlerDayNClick,
    compareRoas,
  ])

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 mt-2">
        <Button onClick={handleExportCsv}>Export CSV</Button>
        <div className="text-destructive text-nowrap xs:text-xs xs:text-wrap lg:text-base">
          DN的数字是由最近的30个有效数据计算得出(原始数据计算平均)。
        </div>
        <Toggles />
      </div>
      <div className="rounded-md border mt-2" style={{ height: '600px' }}>
        <ROASDailyDataTable
          dataSource={sortedData}
          columns={columns}
          features={{ sort: { sorts, onChange: handleSort } }}
          primaryKey="rowId"
        />
      </div>
      <ChartCodeViewer
        open={isChartModalOpen}
        onOpenChange={setIsChartModalOpen}
        hideButton={true}
      >
        <RetChart
          roas={roas}
          compareRoas={compareRoas}
          dayIndex={chartDayIndex}
        />
      </ChartCodeViewer>
    </>
  )
}

const Toggles = () => {
  const {
    retDisplayMode,
    userSourceType,
    denominatorSource,
    setRetDisplayMode,
    setUserSourceType,
    setDenominatorSource,
    dayOrder,
    setDayOrder,
  } = useRoasStore()
  return (
    <div className="flex gap-2 ml-auto">
      <Button
        variant="outline"
        size="sm"
        className="h-9 bg-muted text-muted-foreground border-none hover:bg-muted/80"
        onClick={() => setDayOrder(dayOrder === 'asc' ? 'desc' : 'asc')}
      >
        {dayOrder === 'asc' ? 'D0 → DN' : 'DN → D0'}
      </Button>
      <Tabs value={denominatorSource} onValueChange={setDenominatorSource}>
        <TabsList>
          <TabsTrigger value="AF">AF</TabsTrigger>
          <TabsTrigger value="Firebase">Firebase</TabsTrigger>
        </TabsList>
      </Tabs>
      <Tabs value={retDisplayMode} onValueChange={setRetDisplayMode}>
        <TabsList>
          <TabsTrigger value="number">#</TabsTrigger>
          <TabsTrigger value="percentage">%</TabsTrigger>
        </TabsList>
      </Tabs>
      <Tabs value={userSourceType} onValueChange={setUserSourceType}>
        <TabsList>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  )
}

export default RetTable
